import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import DashboardNav from '../../components/DashboardNav';
import {
  Plus,
  Search,
  Users,
  Briefcase,
  Target,
  Settings2,
  Wallet,
  ChevronRight,
  Loader2,
  BarChart3,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Type icons and colors
const TYPE_CONFIG = {
  functional: { icon: Settings2, color: 'bg-blue-500/10 text-blue-400 border-blue-500/30', label: 'Functional' },
  team: { icon: Users, color: 'bg-purple-500/10 text-purple-400 border-purple-500/30', label: 'Team' },
  domain: { icon: Briefcase, color: 'bg-green-500/10 text-green-400 border-green-500/30', label: 'Domain' },
  process: { icon: Target, color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', label: 'Process' },
  budget: { icon: Wallet, color: 'bg-pink-500/10 text-pink-400 border-pink-500/30', label: 'Budget' }
};

const AreasPage = () => {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [areaTypes, setAreaTypes] = useState([]);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchAreas();
    fetchSummary();
    fetchTypes();
  }, [showArchived]);

  const fetchAreas = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/dashboard/areas?include_archived=${showArchived}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAreas(data.areas || []);
      }
    } catch (error) {
      console.error('Error fetching areas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/areas/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/areas/types`);
      if (response.ok) {
        const data = await response.json();
        setAreaTypes(data.types || []);
      }
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  };

  const handleCreateArea = async (newArea) => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/areas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newArea)
      });
      
      if (response.ok) {
        const created = await response.json();
        setAreas(prev => [created, ...prev]);
        setShowCreateModal(false);
        fetchSummary();
      }
    } catch (error) {
      console.error('Error creating area:', error);
    }
  };

  // Filter areas
  const filteredAreas = areas.filter(area => {
    const matchesSearch = area.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         area.scope?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || area.type === filterType;
    return matchesSearch && matchesType;
  });

  const formatBudget = (amount, currency = 'GBP') => {
    if (!amount) return null;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <DashboardNav />
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <DashboardNav />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6" data-testid="areas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Areas of Responsibility</h1>
          <p className="text-slate-400 text-sm mt-1">
            What you're accountable for on an ongoing basis
          </p>
        </div>
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-500" data-testid="add-area-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Area
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Add Area of Responsibility</DialogTitle>
            </DialogHeader>
            <CreateAreaForm 
              types={areaTypes} 
              onSubmit={handleCreateArea}
              onCancel={() => setShowCreateModal(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      {summary && summary.total_active > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-white">{summary.total_active}</div>
              <div className="text-xs text-slate-400">Active Areas</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-white">{summary.total_headcount}</div>
              <div className="text-xs text-slate-400">Total Headcount</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-white">
                {formatBudget(summary.total_budget, summary.currency) || '—'}
              </div>
              <div className="text-xs text-slate-400">Total Budget</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-white">
                {Object.keys(summary.by_type || {}).length}
              </div>
              <div className="text-xs text-slate-400">Area Types</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search areas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900/50 border-slate-800 text-white"
            data-testid="search-areas"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-40 bg-slate-900/50 border-slate-800 text-white" data-testid="filter-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all" className="text-slate-300">All Types</SelectItem>
            {areaTypes.map(type => (
              <SelectItem key={type.value} value={type.value} className="text-slate-300">
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => setShowArchived(!showArchived)}
          className={`border-slate-700 ${showArchived ? 'bg-slate-800 text-white' : 'text-slate-400'}`}
          data-testid="toggle-archived"
        >
          {showArchived ? 'Hide Archived' : 'Show Archived'}
        </Button>
      </div>

      {/* Areas List */}
      {filteredAreas.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              {areas.length === 0 ? 'No areas yet' : 'No matching areas'}
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              {areas.length === 0 
                ? 'Add your first area of responsibility to track what you own.'
                : 'Try adjusting your search or filters.'}
            </p>
            {areas.length === 0 && (
              <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-500">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Area
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredAreas.map(area => {
            const typeConfig = TYPE_CONFIG[area.type] || TYPE_CONFIG.functional;
            const TypeIcon = typeConfig.icon;
            
            return (
              <Card
                key={area.area_id}
                className={`bg-slate-900/50 border-slate-800 hover:border-slate-700 cursor-pointer transition-all ${
                  area.status === 'archived' ? 'opacity-60' : ''
                }`}
                onClick={() => navigate(`/dashboard/areas/${area.area_id}`)}
                data-testid={`area-card-${area.area_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${typeConfig.color.split(' ')[0]}`}>
                        <TypeIcon className={`w-5 h-5 ${typeConfig.color.split(' ')[1]}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-white truncate">{area.name}</h3>
                          {area.status === 'archived' && (
                            <Badge variant="outline" className="border-slate-600 text-slate-500 text-xs">
                              Archived
                            </Badge>
                          )}
                        </div>
                        <Badge variant="outline" className={`${typeConfig.color} text-xs mb-2`}>
                          {typeConfig.label}
                        </Badge>
                        <p className="text-slate-400 text-sm line-clamp-2">{area.scope}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-600 flex-shrink-0" />
                  </div>
                  
                  {/* Stats row */}
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-800">
                    {area.headcount > 0 && (
                      <div className="flex items-center gap-1.5 text-sm">
                        <Users className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-300">{area.headcount}</span>
                      </div>
                    )}
                    {area.budget_opex > 0 && (
                      <div className="flex items-center gap-1.5 text-sm">
                        <Wallet className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-300">{formatBudget(area.budget_opex, area.currency)}</span>
                      </div>
                    )}
                    {area.key_metrics?.length > 0 && (
                      <div className="flex items-center gap-1.5 text-sm">
                        <BarChart3 className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-300">{area.key_metrics.length} metrics</span>
                      </div>
                    )}
                    {area.decision_authority?.length > 0 && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-500">
                        <Target className="w-4 h-4" />
                        <span>{area.decision_authority.length} authorities</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
        </div>
      </main>
    </div>
  );
};

// Create Area Form Component
const CreateAreaForm = ({ types, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'functional',
    scope: '',
    headcount: '',
    budget_opex: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.scope.trim()) {
      setError('Name and scope are required');
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      name: formData.name.trim(),
      type: formData.type,
      scope: formData.scope.trim()
    };

    if (formData.headcount) {
      payload.headcount = parseInt(formData.headcount);
    }
    if (formData.budget_opex) {
      payload.budget_opex = parseFloat(formData.budget_opex);
    }

    try {
      await onSubmit(payload);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-slate-300">Area Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Platform Reliability, Backend Team"
          className="bg-slate-800/50 border-slate-700 text-white"
          data-testid="area-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="type" className="text-slate-300">Type *</Label>
        <Select 
          value={formData.type} 
          onValueChange={(val) => setFormData(prev => ({ ...prev, type: val }))}
        >
          <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white" data-testid="area-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {types.map(type => (
              <SelectItem key={type.value} value={type.value} className="text-slate-300">
                <div>
                  <div>{type.label}</div>
                  <div className="text-xs text-slate-500">{type.description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="scope" className="text-slate-300">Scope / Description *</Label>
        <Textarea
          id="scope"
          value={formData.scope}
          onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value }))}
          placeholder="What does this area cover? What are you accountable for?"
          className="bg-slate-800/50 border-slate-700 text-white min-h-[80px]"
          data-testid="area-scope-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="headcount" className="text-slate-300">Headcount</Label>
          <Input
            id="headcount"
            type="number"
            min="0"
            value={formData.headcount}
            onChange={(e) => setFormData(prev => ({ ...prev, headcount: e.target.value }))}
            placeholder="0"
            className="bg-slate-800/50 border-slate-700 text-white"
            data-testid="area-headcount-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="budget" className="text-slate-300">Annual Budget (£)</Label>
          <Input
            id="budget"
            type="number"
            min="0"
            value={formData.budget_opex}
            onChange={(e) => setFormData(prev => ({ ...prev, budget_opex: e.target.value }))}
            placeholder="0"
            className="bg-slate-800/50 border-slate-700 text-white"
            data-testid="area-budget-input"
          />
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="border-slate-700 text-slate-300">
          Cancel
        </Button>
        <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-500" data-testid="create-area-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Area'}
        </Button>
      </div>
    </form>
  );
};

export default AreasPage;
